const { Core, Utils } = Global;
const { $fetch } = Utils;

const PRODUCTS_CONTAINER = '[data-element="products-container"]';

/**
 * Sub-collection browser.
 *
 * One fetch per product type through the Section Rendering API against
 * s_subcollection-grid, cached, so the preview fill and the later
 * expand share a single request. Cards are whatever product-card
 * renders — this component never builds card markup.
 */
customElements.define(
    'subcollection-browser',
    class extends Core {
        elements = {
            $: [['section'], ['chip'], 'nav', 'chips']
        };

        propTypes = {
            'collection-url': String,
            'grid-section-id': String,
            'preview-count': Number,
            'sort-by': String,
            columns: Number
        };

        render() {
            this.cache = new Map();
            this.sections = this.$('section') || [];
            this.chips = this.$('chip') || [];
            this.previewCount = this.prop('preview-count') || 4;

            this._measure();
            this._observeSections();
            this._bindControls();
            this._bindNav();
        }

        /* ---- sticky offsets ------------------------------------------ */
        // The toolbar is already sticky at --header-height + --navbar-height;
        // publish its height (and this nav's) so the nav stacks under it and
        // scroll targets clear both.
        _measure() {
            const toolbar = document.querySelector('[class*="collection-toolbar"]');
            const nav = this.$('nav');
            const write = () => {
                const root = document.documentElement.style;
                // getBoundingClientRect, not offsetHeight: offsetHeight rounds
                // 47.734 up to 48, which parks this nav a quarter-pixel below the
                // toolbar's bottom edge and lets the page show through the seam.
                if (toolbar) {
                    const pinned = getComputedStyle(toolbar).position === 'sticky';
                    root.setProperty(
                        '--collection-toolbar-height',
                        pinned ? `${toolbar.getBoundingClientRect().height}px` : '0px'
                    );
                
                }
                if (nav) {
                    root.setProperty('--subcollections-nav-height', `${nav.getBoundingClientRect().height}px`);
                }
            };
            write();
            // The webfont swap changes both bars' height after first paint, and
            // the ResizeObserver below doesn't always catch it.
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(write);
            window.setTimeout(write, 150);
            if ('ResizeObserver' in window) {
                this.ro = new ResizeObserver(write);
                toolbar && this.ro.observe(toolbar);
                nav && this.ro.observe(nav);
            }
            window.addEventListener('resize', write, { passive: true });
        }

        get offsetTop() {
            const read = (name) =>
                parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
            return (
                read('--header-height') +
                read('--navbar-height') +
                read('--collection-toolbar-height') +
                read('--subcollections-nav-height')
            );
        }

        /* ---- lazy fill ----------------------------------------------- */
        _observeSections() {
            if (!('IntersectionObserver' in window)) {
                this.sections.forEach((section) => this._fill(section));
                return;
            }
            this.io = new IntersectionObserver(
                (entries, obs) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        obs.unobserve(entry.target);
                        this._fill(entry.target);
                    });
                },
                { rootMargin: '600px 0px' }
            );
            this.sections.forEach((section) => this.io.observe(section));
        }

        async _cards(type) {
            if (this.cache.has(type)) return this.cache.get(type);

            const params = new URLSearchParams({
                section_id: this.prop('grid-section-id'),
                'filter.p.product_type': type
            });
            const sort = this.prop('sort-by');
            if (sort && sort !== 'manual') params.set('sort_by', sort);

            const container = await $fetch(this.prop('collection-url'), {
                params,
                select: PRODUCTS_CONTAINER
            });
            const cards = container ? Array.from(container.children) : [];
            this.cache.set(type, cards);
            return cards;
        }

        async _fill(section) {
            const grid = section.querySelector('[data-element="grid"]');
            if (!grid || section.dataset.state === 'filled') return;

            section.dataset.state = 'loading';
            const cards = await this._cards(section.dataset.type);

            if (!cards.length) {
                // Nothing came back — leave the noscript link as the way through.
                section.dataset.state = 'error';
                grid.replaceChildren();
                return;
            }

            grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));
            Array.from(grid.children).forEach((card, i) => {
                if (i >= this.previewCount) card.hidden = true;
            });
            section.dataset.state = 'filled';
        }

        /* ---- expand -------------------------------------------------- */
        _bindControls() {
            this.sections.forEach((section) => {
                const expand = section.querySelector('[data-element="expand"]');
                const more = section.querySelector('[data-element="more"]');
                const run = async (event) => {
                    event.preventDefault();
                    await this._expand(section, more, expand);
                };
                expand && expand.addEventListener('click', run);
                more && more.addEventListener('click', run);
            });
        }

        async _expand(section, more, expand) {
            const grid = section.querySelector('[data-element="grid"]');
            if (!grid || section.dataset.expanded === 'true') return;

            if (more) {
                more.setAttribute('aria-busy', 'true');
                more.textContent = more.dataset.loadingLabel || 'Loading…';
            }

            if (section.dataset.state !== 'filled') {
                await this._fill(section);
            }

            const cards = await this._cards(section.dataset.type);
            grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));

            section.dataset.expanded = 'true';
            expand && expand.setAttribute('aria-expanded', 'true');
            expand && expand.remove();

            const total = parseInt(section.dataset.count, 10) || cards.length;
            if (more) {
                more.removeAttribute('aria-busy');
                // A type with more than one Liquid page (50) keeps a way through
                // to the fully filtered view rather than fetching page after page.
                if (cards.length < total) {
                    more.textContent = more.dataset.label;
                    section.dataset.expanded = 'false';
                    more.dataset.page = String((parseInt(more.dataset.page, 10) || 1) + 1);
                } else {
                    more.remove();
                }
            }
        }

        /* ---- jump nav ------------------------------------------------ */
        _bindNav() {
            if (!this.chips.length) return;

            this.chips.forEach((chip) => {
                chip.addEventListener('click', (event) => {
                    const target = document.getElementById(chip.dataset.target);
                    if (!target) return;
                    event.preventDefault();
                    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    window.scrollTo({
                        top: target.getBoundingClientRect().top + window.scrollY - this.offsetTop - 8,
                        behavior: reduce ? 'auto' : 'smooth'
                    });
                });
            });

            this.onScroll = () => {
                if (this.rafId) return;
                this.rafId = requestAnimationFrame(() => {
                    this.rafId = null;
                    this._syncActive();
                });
            };
            window.addEventListener('scroll', this.onScroll, { passive: true });
            this._syncActive();
        }

        _syncActive() {
            const line = this.offsetTop + 80;
            let active = 0;
            this.sections.forEach((section, i) => {
                if (section.getBoundingClientRect().top <= line) active = i;
            });
            this.chips.forEach((chip, i) => {
                const on = i === active;
                on ? chip.setAttribute('aria-current', 'true') : chip.removeAttribute('aria-current');
                if (on) this._revealChip(chip);
            });
        }

        _revealChip(chip) {
            const row = this.$('chips');
            if (!row || row.scrollWidth <= row.clientWidth) return;
            const chipBox = chip.getBoundingClientRect();
            const rowBox = row.getBoundingClientRect();
            if (chipBox.left < rowBox.left || chipBox.right > rowBox.right) {
                row.scrollLeft += chipBox.left - rowBox.left - 24;
            }
        }

        disconnectedCallback() {
            super.disconnectedCallback?.();
            this.io?.disconnect();
            this.ro?.disconnect();
            this.onScroll && window.removeEventListener('scroll', this.onScroll);
        }
    }
);
